import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {apiClient} from '../restClient'
import {
  GENERATE_TOKEN_ENDPOINT,
  GET_ME_ENDPOINT,
  GET_PRODUCT_SWITCHER_ENDPOINT,
  GET_WORKFORCES_ENDPOINT,
  UPDATE_USER_SETTINGS_ENDPOINT,
} from './endPoints'
import {PerPageOneThousand} from '../constants/perPageConstants'
import type {IProductSwitcherResponse, IUser, IWorkforce} from '../types'

interface ITokenResponse {
  access_token: string
  refresh_token: string
}

const fetchCurrentUser = (): Promise<IUser> =>
  apiClient.get(GET_ME_ENDPOINT).then(r => r.data)

const fetchAllWorkforces = (): Promise<{response: IWorkforce[]}> =>
  apiClient
    .get(GET_WORKFORCES_ENDPOINT, {
      params: {offset: 0, limit: PerPageOneThousand},
    })
    .then(r => r.data)

const fetchProductSwitcher = (): Promise<IProductSwitcherResponse> =>
  apiClient.get(GET_PRODUCT_SWITCHER_ENDPOINT).then(r => r.data)

export const userApi = {
  useCurrentUser: (token?: string) =>
    useQuery({
      queryKey: ['user', 'me'],
      queryFn: fetchCurrentUser,
      enabled: !!token,
      staleTime: Infinity,
      // Keep the backend AuthGuard 5-min token cache warm to prevent 401 on stale-free routes.
      refetchInterval: 4 * 60 * 1000,
    }),

  useWorkforces: (token?: string) =>
    useQuery({
      queryKey: ['user', 'workforces'],
      queryFn: fetchAllWorkforces,
      enabled: !!token,
      staleTime: Infinity,
    }),

  useProductSwitcher: (enabled: boolean) =>
    useQuery({
      queryKey: ['user', 'productSwitcher'],
      queryFn: fetchProductSwitcher,
      enabled,
      staleTime: Infinity,
      retry: false,
    }),

  useUpdateUserSettings: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {language: string}): Promise<IUser> =>
        apiClient.put(UPDATE_USER_SETTINGS_ENDPOINT, payload).then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['user']}),
    })
  },

  useGenerateToken: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        code,
        portalUrl,
      }: {
        code: string
        portalUrl?: string
      }): Promise<ITokenResponse> =>
        apiClient
          .get(GENERATE_TOKEN_ENDPOINT, {
            params: {code, ...(portalUrl && {portalUrl})},
          })
          .then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['user']}),
    })
  },
} as const
